// ---------------------------------------------------------------
// Rental agreement text. One source of truth for the on-screen contract
// preview, the stored contract snapshot, and the printable PDF. The renter
// accepts full liability for the vehicle, tickets, tolls, and anything else
// that happens during the rental, then signs.
// ---------------------------------------------------------------

export const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dt = (d) => (d ? new Date(d).toLocaleString([], { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const dOnly = (d) => (d ? new Date(d).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

// ctx: { company, vehicle, vin, plate, renter, license, phone, email,
//        startAt, dueAt, days, dailyPrice, includedMilesPerDay, overageRate,
//        deposit, startOdoMi }
export function agreementTerms(ctx) {
  return [
    ['Vehicle', ctx.vehicle || '—'],
    ['VIN', ctx.vin || '—'],
    ['Plate', ctx.plate || '—'],
    ['Renter', ctx.renter || '—'],
    ["Driver's license #", ctx.license || '—'],
    ['Phone', ctx.phone || '—'],
    ['Email', ctx.email || '—'],
    ['Rental starts', dt(ctx.startAt)],
    ['Due back', dt(ctx.dueAt)],
    ['Booked term', `${ctx.days || 0} day(s)`],
    ...(ctx.returnedAt ? [['Returned', dt(ctx.returnedAt)]] : []),
    ...((ctx.actualDays != null && ctx.actualDays !== ctx.days)
      ? [['Actual days out', `${ctx.actualDays} day(s)${ctx.status === 'closed' ? '' : ' (so far)'}${ctx.actualDays > ctx.days ? ' — kept beyond booked term' : ''}`]]
      : []),
    ['Daily rate', money(ctx.dailyPrice)],
    ['Included miles', `${ctx.includedMilesPerDay || 0} mi/day`],
    ['Overage rate', `${money(ctx.overageRate)} / mile over`],
    ['Security deposit', money(ctx.deposit)],
    ['Start odometer', ctx.startOdoMi != null ? `${Math.round(ctx.startOdoMi).toLocaleString()} mi` : '—'],
  ];
}

// The clauses the renter agrees to. Kept explicit so it renders identically
// in the preview and the printed PDF.
export function liabilityClauses(ctx) {
  const over = money(ctx.overageRate);
  const inc = ctx.includedMilesPerDay || 0;
  return [
    ['Full responsibility for the vehicle',
      'The renter accepts full responsibility for the vehicle from pickup until it is returned, and is liable for ANY and ALL loss of or damage to the vehicle during the rental — including collision, theft, vandalism, fire, weather, towing, glass, tires, interior damage, and mechanical damage caused by misuse — regardless of fault or cause.'],
    ['Traffic tickets & citations',
      'The renter is responsible for all traffic, parking, red-light, speed-camera, and other citations or violations issued during the rental period, plus any related administrative or processing fees.'],
    ['Tolls',
      'The renter is responsible for all tolls incurred during the rental period and any related administrative or processing fees.'],
    ['Mileage',
      `Included mileage is ${inc} miles per rental day. Miles driven beyond the included allowance are billed at ${over} per mile.`],
    ['Fuel',
      'The vehicle must be returned with the same fuel level as at pickup. Otherwise a refueling charge applies.'],
    ['Late return',
      'The vehicle must be returned by the due date/time. Late returns are billed at the daily rate (or a prorated amount) for each additional day or part thereof.'],
    ['Authorized use & drivers',
      'Only the renter (and any driver approved in writing) may operate the vehicle, each holding a valid license. The vehicle may not be used for any illegal purpose, to carry passengers or property for hire, in any race or contest, while the driver is under the influence of alcohol or drugs, or outside any agreed area.'],
    ['Loss, theft, impound & recovery',
      'The renter is responsible for costs arising from loss, theft, impound, repossession, or recovery of the vehicle, including administrative and locating fees.'],
    ['Security deposit',
      'The security deposit may be applied toward any amounts the renter owes under this agreement — including damage, citations, tolls, cleaning, fuel, late fees, and overage — with the balance refunded. A written reason is provided for any amount withheld.'],
    ['Cleaning & condition',
      'The vehicle must be returned in the same condition as received. Excessive dirt, odor (including smoking), stains, or damage may incur a cleaning or reconditioning charge.'],
    ['Payment authorization',
      'The renter authorizes the owner/agent to charge the renter’s payment method on file for all amounts due under this agreement, including damage, citations, tolls, fuel, cleaning, late, and overage charges.'],
    ['Indemnification',
      'The renter agrees to indemnify and hold harmless the owner and their agents from any claim, loss, liability, or expense arising out of the renter’s use or possession of the vehicle.'],
    ['Anything else pertaining',
      'The renter is responsible for any other cost, fee, fine, or liability of any kind arising from or pertaining to the use, possession, or operation of the vehicle during the rental period.'],
  ];
}

// A plain-text snapshot stored with the rental so the exact wording the renter
// agreed to is preserved even if the template changes later.
export function contractPlainText(ctx) {
  const terms = agreementTerms(ctx).map(([k, v]) => `  ${k}: ${v}`).join('\n');
  const clauses = liabilityClauses(ctx).map(([h, t], i) => `${i + 1}. ${h}\n   ${t}`).join('\n\n');
  return `${(ctx.company || 'VEHICLE RENTAL AGREEMENT').toUpperCase()}\n\nTERMS\n${terms}\n\nRENTER RESPONSIBILITY & LIABILITY\n${clauses}\n\nBy signing below, the renter acknowledges they have read, understood, and agree to this agreement in full, and accept liability for the vehicle and all charges described above.`;
}

export { dOnly };
